/**
 * Weekly counts of reports created in each module.
 *
 * Stored values are only the module, the Monday of the UTC week, and the
 * count. Account, project, and property are not part of this payload and are
 * dropped if a response includes them. Weeks older than 13 months are ignored.
 *
 * The API keeps the same buckets:
 * POST /module-report-counts  { "module": "<key>" }
 * GET  /admin/module-report-counts
 *   { "weeks": [{ "week_start": "YYYY-MM-DD", "module": "<key>", "count": 0 }] }
 */

export const MODULE_REPORT_RETENTION_MONTHS = 13;

export const REPORT_MODULES = [
  { key: "content_optimization", label: "Content Optimization" },
  { key: "content_decay", label: "Content Decay" },
  { key: "traffic_trend", label: "Traffic Trend" },
  { key: "optimization_wizard", label: "Optimization Wizard" },
  { key: "keyword_map", label: "Keyword Map" },
  { key: "api_scheduler", label: "API Scheduler" },
  { key: "traffic_insights", label: "Traffic Insights" },
  { key: "ad_hoc_export", label: "Ad Hoc Export" },
] as const;

export type ReportModule = typeof REPORT_MODULES[number]["key"];

export type ModuleReportCount = {
  weekStart: string;
  module: ReportModule;
  count: number;
};

const MODULE_KEYS = new Set<string>(REPORT_MODULES.map((module) => module.key));

export function isReportModule(value: string): value is ReportModule {
  return MODULE_KEYS.has(value);
}

/** Body sent when a report is created. Only the module key is included. */
export function moduleReportCountBody(module: ReportModule): { module: ReportModule } {
  return { module };
}

export function previousWeekStart(now: Date): string {
  const today = utcDate(now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate());
  const monday = utcMonday(today);
  monday.setUTCDate(monday.getUTCDate() - 7);
  return formatUtc(monday);
}

export function retentionStart(now: Date): string {
  const cutoff = utcDate(now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate());
  cutoff.setUTCMonth(cutoff.getUTCMonth() - MODULE_REPORT_RETENTION_MONTHS);
  return formatUtc(utcMonday(cutoff));
}

export function weekEnd(weekStart: string): string {
  const date = parseUtcDate(weekStart);
  if (!date) {
    return weekStart;
  }
  date.setUTCDate(date.getUTCDate() + 6);
  return formatUtc(date);
}

export function parseModuleReportCounts(payload: unknown, now: Date): ModuleReportCount[] {
  const rows = extractRows(payload);
  if (!rows) {
    throw new Error("Module report counts are missing");
  }

  const earliest = retentionStart(now);
  const merged = new Map<string, ModuleReportCount>();
  rows.forEach((row) => {
    const parsed = parseRow(row, earliest);
    if (!parsed) {
      return;
    }
    const key = `${parsed.weekStart}|${parsed.module}`;
    const existing = merged.get(key);
    if (existing) {
      existing.count += parsed.count;
      return;
    }
    merged.set(key, parsed);
  });
  return Array.from(merged.values());
}

export function countsForWeek(
  rows: ModuleReportCount[],
  weekStart: string
): Record<ReportModule, number> {
  const counts = {} as Record<ReportModule, number>;
  REPORT_MODULES.forEach((module) => {
    counts[module.key] = 0;
  });
  rows.forEach((row) => {
    if (row.weekStart === weekStart) {
      counts[row.module] += row.count;
    }
  });
  return counts;
}

/** Weeks that have at least one report, newest first. */
export function storedWeekStarts(rows: ModuleReportCount[]): string[] {
  const weeks = new Set<string>();
  rows.forEach((row) => {
    if (row.count > 0) {
      weeks.add(row.weekStart);
    }
  });
  return Array.from(weeks).sort((left, right) => (left < right ? 1 : left > right ? -1 : 0));
}

function extractRows(payload: unknown): unknown[] | null {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && typeof payload === "object" && Array.isArray((payload as { weeks?: unknown }).weeks)) {
    return (payload as { weeks: unknown[] }).weeks;
  }
  return null;
}

function parseRow(row: unknown, earliest: string): ModuleReportCount | null {
  if (!row || typeof row !== "object") {
    return null;
  }
  const record = row as { week_start?: unknown; module?: unknown; count?: unknown };
  if (typeof record.week_start !== "string" || typeof record.module !== "string") {
    return null;
  }
  if (!isReportModule(record.module) || !isUtcMonday(record.week_start)) {
    return null;
  }
  if (record.week_start < earliest) {
    return null;
  }
  if (typeof record.count !== "number" || !Number.isInteger(record.count) || record.count < 0) {
    return null;
  }
  return {
    weekStart: record.week_start,
    module: record.module,
    count: record.count,
  };
}

function isUtcMonday(iso: string): boolean {
  const date = parseUtcDate(iso);
  return date !== null && date.getUTCDay() === 1;
}

function parseUtcDate(iso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = utcDate(year, month, day);
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

function utcDate(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month - 1, day));
}

function utcMonday(date: Date): Date {
  const monday = new Date(date.getTime());
  const day = monday.getUTCDay();
  const sinceMonday = day === 0 ? 6 : day - 1;
  monday.setUTCDate(monday.getUTCDate() - sinceMonday);
  return monday;
}

function formatUtc(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
